import { events } from "@constants/tracking";
import useAnalytics from "@hooks/useAnalytics";
import { useLocale } from "@hooks/useLocale";
import type { Language } from "@memorang/constants/i18n";
import DialogWrapper from "@memorang/ui/components/DialogWrapper";
import { useToast } from "@memorang/ui/components/Toast";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, useWindowDimensions } from "react-native";
import { Button, Dialog, List, RadioButton } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";

type Props = {
	visible: boolean;
	handleClose: () => void;
};

const LanguageDialog = ({ visible, handleClose }: Props) => {
	const { t, i18n } = useTranslation();
	const { currentLocale, availableLanguages, changeLocale } = useLocale();
	const { explicitCallTrackCustomEvent } = useAnalytics();
	const { showToast } = useToast();
	const { height: windowHeight } = useWindowDimensions();
	const insets = useSafeAreaInsets();
	const [selectedLanguage, setSelectedLanguage] = useState(currentLocale);

	const maxHeight = (windowHeight - insets.top - insets.bottom) * 0.9;

	const handleLanguageChange = async (code: string) => {
		const prevLanguage =
			availableLanguages.find((language) => language.code === currentLocale)
				?.label ?? currentLocale;
		const newLanguage =
			availableLanguages.find((language) => language.code === code)?.label ??
			code;

		await changeLocale(code);
		handleClose();
		explicitCallTrackCustomEvent({
			eventName: events.userPreferenceChanged,
			newLanguage,
			currentLanguage: prevLanguage,
		});
		showToast({ message: i18n.t("language.changeSuccess") });
	};

	const LanguageItem = ({ code, label }: Language) => (
		<List.Item
			onPress={() => setSelectedLanguage(code)}
			title={label}
			right={() => <RadioButton value={code} />}
		/>
	);

	return (
		<DialogWrapper
			visible={visible}
			handleClose={handleClose}
			width="90%"
			maxWidth={400}
			style={{ maxHeight }}
		>
			<Dialog.Icon icon="translate" />
			<Dialog.Title style={{ textAlign: "center" }}>
				{t("language.select")}
			</Dialog.Title>
			<Dialog.ScrollArea style={{ paddingHorizontal: 0 }}>
				<ScrollView>
					<RadioButton.Group
						value={selectedLanguage}
						onValueChange={setSelectedLanguage}
					>
						{availableLanguages.map((language) => (
							<LanguageItem key={language.code} {...language} />
						))}
					</RadioButton.Group>
				</ScrollView>
			</Dialog.ScrollArea>
			<Dialog.Actions>
				<Button onPress={handleClose} style={{ minWidth: 72 }}>
					{t("common.cancel")}
				</Button>
				<Button
					mode="contained"
					onPress={() => handleLanguageChange(selectedLanguage)}
					disabled={selectedLanguage === currentLocale}
					style={{ minWidth: 100 }}
				>
					{t("common.save")}
				</Button>
			</Dialog.Actions>
		</DialogWrapper>
	);
};

export default LanguageDialog;
